import { Alert, Spinner } from "react-bootstrap";
import FastForm from "../../components/FastForm/FastForm";
import { usePrecargaEvents } from "../../hooks/usePrecargaEvents";
import {
  isoToLocalInput,
  pesosTocentavos,
  centavosToPesos,
  validatePrecargaDates,
  validatePrecargaLimits,
} from "../../utils/precargaHelpers";

export default function EditPrecargaEventForm({ eventId, onSuccess, onCancel }) {
  const { event, isLoadingEvent, updateMutation } = usePrecargaEvents({ eventId });

  if (isLoadingEvent) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p>Cargando evento...</p>
      </div>
    );
  }

  if (!event) {
    return <Alert variant="danger">No se encontró el evento</Alert>;
  }

  return (
    <div>
      <FastForm
        key={event.id}
        columns={[1,2,4]}
        containerStyle={{ maxWidth: "1200px" }}
        mutation={{
          ...updateMutation,
          mutate: (data) => updateMutation.mutate({ eventId: event.id, data }),
        }}
        sendText="Guardar Cambios"
        cancelText="Cancelar"
        onCancel={onCancel}
        fields={[
          {
            type: "divider",
            title: "Evento",
          },
          {
            name: "slug",
            label: "Slug (URL amigable)",
            type: "text",
            defaultValue: event.slug,
            attributes: {
              required: true,
              pattern: "[a-z0-9-]+",
            },
            help: "Solo letras minúsculas, números y guiones",
          },
          {
            name: "status",
            label: "Estado",
            type: "select",
            defaultValue: event.status,
            options: [
              { value: "ACTIVE", label: "Activo" },
              { value: "DISABLED", label: "Deshabilitado" },
            ],
            attributes: { required: true },
          },
          {
            name: "disabledReason",
            label: "Razón de Deshabilitación",
            type: "textarea",
            defaultValue: event.disabledReason || "",
            attributes: {
              rows: 3,
            },
            help: "Opcional: motivo por el cual se deshabilita el evento",
          },
          {
            type: "divider",
            title: "Carga",
          },
          {
            name: "currency",
            label: "Moneda",
            type: "select",
            defaultValue: event.currency,
            options: [
              { value: "ARS", label: "Peso Argentino (ARS)" },
              { value: "BRL", label: "Real Brasileño (BRL)" },
              { value: "CLP", label: "Peso Chileno (CLP)" },
              { value: "COP", label: "Peso Colombiano (COP)" },
              { value: "EUR", label: "Euro (EUR)" },
              { value: "PYG", label: "Guaraní Paraguayo (PYG)" },
              { value: "MXN", label: "Peso Mexicano (MXN)" },
              { value: "USD", label: "Dólar (USD)" },
              { value: "UYU", label: "Peso Uruguayo (UYU)" },
            ],
            attributes: { required: true },
          },
          {
            name: "minTopup",
            label: "Monto Mínimo de Recarga (en pesos)",
            type: "number",
            defaultValue: centavosToPesos(event.minTopup),
            attributes: {
              required: true,
              min: 0,
              step: 0.01,
            },
            prefix: "$",
            help: "Monto mínimo que puede recargar un usuario",
          },
          {
            name: "maxTopup",
            label: "Monto Máximo de Recarga (en pesos)",
            type: "number",
            defaultValue: centavosToPesos(event.maxTopup),
            attributes: {
              required: true,
              min: 0,
              step: 0.01,
            },
            prefix: "$",
            help: "Monto máximo que puede recargar un usuario",
          },
          {
            type: "divider",
            title: "Períodos de Operación",
          },
          {
            name: "preloadOpenAt",
            label: "Apertura de Precargas",
            type: "datetime-local",
            defaultValue: event.preloadOpenAt
              ? isoToLocalInput(event.preloadOpenAt)
              : "",
            attributes: { required: false },
            help: "Fecha y hora de inicio para realizar precargas",
          },
          {
            name: "preloadCloseAt",
            label: "Cierre de Precargas",
            type: "datetime-local",
            defaultValue: event.preloadCloseAt
              ? isoToLocalInput(event.preloadCloseAt)
              : "",
            attributes: { required: false },
            help: "Fecha y hora de cierre para realizar precargas",
          },
          {
            name: "refundOpenAt",
            label: "Apertura de Devoluciones",
            type: "datetime-local",
            defaultValue: event.refundOpenAt
              ? isoToLocalInput(event.refundOpenAt)
              : "",
            attributes: { required: false },
            help: "Fecha y hora de inicio para solicitar devoluciones",
          },
          {
            name: "refundCloseAt",
            label: "Cierre de Devoluciones",
            type: "datetime-local",
            defaultValue: event.refundCloseAt
              ? isoToLocalInput(event.refundCloseAt)
              : "",
            attributes: { required: false },
            help: "Fecha y hora de cierre para solicitar devoluciones",
          },
        ]}
        validate={(data) => {
          // Validate dates
          const dateValidation = validatePrecargaDates(data);
          if (dateValidation !== true) {
            return dateValidation;
          }

          // Validate limits
          const limitsValidation = validatePrecargaLimits({
            minTopup: pesosTocentavos(data.minTopup),
            maxTopup: pesosTocentavos(data.maxTopup),
          });
          if (limitsValidation !== true) {
            return limitsValidation;
          }

          return true;
        }}
        parseData={(data) => {
          // Convert pesos to centavos
          const parsedData = {
            ...data,
            minTopup: pesosTocentavos(data.minTopup),
            maxTopup: pesosTocentavos(data.maxTopup),
          };

          // Convert dates to ISO strings (or null if empty)
          if (data.preloadOpenAt) {
            parsedData.preloadOpenAt = new Date(data.preloadOpenAt).toISOString();
          } else {
            parsedData.preloadOpenAt = null;
          }

          if (data.preloadCloseAt) {
            parsedData.preloadCloseAt = new Date(data.preloadCloseAt).toISOString();
          } else {
            parsedData.preloadCloseAt = null;
          }

          if (data.refundOpenAt) {
            parsedData.refundOpenAt = new Date(data.refundOpenAt).toISOString();
          } else {
            parsedData.refundOpenAt = null;
          }

          if (data.refundCloseAt) {
            parsedData.refundCloseAt = new Date(data.refundCloseAt).toISOString();
          } else {
            parsedData.refundCloseAt = null;
          }

          // Remove empty disabledReason if status is ACTIVE
          if (data.status === "ACTIVE") {
            delete parsedData.disabledReason;
          }

          return parsedData;
        }}
      />
    </div>
  );
}
